'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { twMerge } from 'tailwind-merge';

type SliderRootBaseProps = React.ComponentProps<typeof SliderPrimitive.Root>;

type SliderProps = Omit<SliderRootBaseProps, 'onChange' | 'value' | 'defaultValue' | 'onValueChange'> & {
	value?: number | number[];
	defaultValue?: number | number[];
	onChange?: (event: number | number[], value: number | number[]) => void;
	marks?: boolean;
};

function toArrayValue(v: SliderProps['value'] | SliderProps['defaultValue'], min: number, max: number) {
	if (Array.isArray(v)) return v;
	if (typeof v === 'number') return [v];
	return [min, max];
}

const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(function Slider(
	{ className, defaultValue, value, min = 0, max = 100, step = 1, onChange, marks, ...props },
	ref,
) {
	const [internalValue, setInternalValue] = React.useState<number[]>(() => toArrayValue(value ?? defaultValue, min, max));

	React.useEffect(() => {
		if (value !== undefined) {
			setInternalValue(toArrayValue(value, min, max));
		}
	}, [value, min, max]);

	React.useEffect(() => {
		if (value === undefined) {
			setInternalValue(toArrayValue(defaultValue, min, max));
		}
	}, [defaultValue, min, max, value]);

	const handleValueChange = (vals: number[]) => {
		setInternalValue(vals);

		if (onChange) {
			const muiValue: number | number[] = vals.length === 1 ? vals[0] : vals;
			onChange(muiValue, muiValue);
		}
	};

	const controlledValue = value !== undefined ? toArrayValue(value, min, max) : undefined;

	const markValues = React.useMemo(() => {
		if (!marks) return [];
		const stepNum = typeof step === 'number' && step > 0 ? step : 1;
		const values: number[] = [];
		for (let v = min; v <= max; v = Number((v + stepNum).toFixed(10))) {
			values.push(v);
		}
		if (values[values.length - 1] !== max) values.push(max);
		return values;
	}, [marks, min, max, step]);

	return (
		<SliderPrimitive.Root
			ref={ref}
			data-slot='slider'
			defaultValue={defaultValue !== undefined ? toArrayValue(defaultValue, min, max) : undefined}
			value={controlledValue}
			min={min}
			max={max}
			step={step}
			onValueChange={handleValueChange}
			className={twMerge(
				'slider-root-empreender relative flex w-full touch-none select-none items-center',
				'data-[orientation=horizontal]:h-5',
				'data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
				'data-[disabled]:opacity-50',
				marks && 'data-[orientation=horizontal]:mb-4',
				className,
			)}
			{...props}>
			<SliderPrimitive.Track
				data-slot='slider-track'
				className={twMerge(
					'relative grow overflow-hidden rounded-full bg-gray-200',
					'data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full',
					'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
				)}>
				<SliderPrimitive.Range
					data-slot='slider-range'
					className={twMerge(
						'absolute bg-gray-900',
						'data-[orientation=horizontal]:h-full',
						'data-[orientation=vertical]:w-full',
					)}
				/>
			</SliderPrimitive.Track>

			{marks &&
				markValues.map((mark) => {
					const pct = max === min ? 0 : ((mark - min) / (max - min)) * 100;
					return (
						<span
							key={mark}
							aria-hidden
							className='pointer-events-none absolute top-full mt-2 size-1 -translate-x-1/2 rounded-full bg-gray-300'
							style={{ left: `${pct}%` }}
						/>
					);
				})}

			{internalValue.map((_, index) => (
				<SliderPrimitive.Thumb
					data-slot='slider-thumb'
					key={index}
					className={twMerge(
						'block size-5 shrink-0 rounded-full border border-gray-900 bg-white shadow-sm !outline-none',
						'transition-[color,box-shadow] ring-gray-900/10 hover:ring-4',
						'disabled:pointer-events-none disabled:opacity-50',
					)}
				/>
			))}
		</SliderPrimitive.Root>
	);
});

export default Slider;
